/**
 * 三套构型（rig）—— 同一组 N 个点的三种排列。
 *
 * 首屏的论点是「阳仔AI，三个方向」：点不生不灭，只是重新排列。
 * 所以三套构型必须共用同一个点数 N，每套负责把这 N 个点摆到自己的目标坐标上。
 * 坐标一律用「以画布中心为原点、按 scale 归一化」的单位，由渲染层换算成像素。
 */

export const N = 126;

export type RGB = [number, number, number];
export const BONE: RGB = [230, 224, 212];
export const AMBER: RGB = [245, 166, 35];
export const EMBER: RGB = [255, 92, 56];
/** 通道色：CH.01 动捕 = 骨白，CH.02 智能体 = 琥珀，CH.03 量化 = 余烬红 */
export const CHANNEL_COLORS: RGB[] = [BONE, AMBER, EMBER];

export const rgba = (c: RGB | number[], a: number) =>
  `rgba(${c[0]},${c[1]},${c[2]},${a})`;

export type Pt = { x: number; y: number };
export type Edge = [number, number];

/* ────────────────────────────────────────────────────────────────
   RIG 1 · POSE —— 人体骨架，两个关键帧之间往复插值
   关键帧是手工摆的，且刻意保持四肢长度一致（上臂/前臂 .105、
   大腿 .152、小腿 .151），否则插值时肢体会像橡皮筋一样伸缩。
   ──────────────────────────────────────────────────────────────── */
export const JN = 15;

const KA: number[][] = [
  [0.035, -0.335], [0.015, -0.255], [-0.058, -0.24], [-0.15, -0.185], [-0.215, -0.095],
  [0.088, -0.23], [0.15, -0.15], [0.12, -0.055], [0.005, 0.045], [-0.04, 0.042],
  [-0.075, 0.19], [-0.055, 0.34], [0.05, 0.048], [0.105, 0.192], [0.13, 0.34],
];
const KB: number[][] = [
  [-0.02, -0.34], [-0.005, -0.258], [-0.082, -0.248], [-0.02, -0.17], [0.06, -0.235],
  [0.078, -0.238], [0.115, -0.135], [0.045, -0.07], [-0.005, 0.045], [-0.048, 0.044],
  [-0.03, 0.192], [-0.075, 0.338], [0.042, 0.046], [0.082, 0.194], [0.118, 0.34],
];

/** 骨骼连接。末两条 [2,9] [5,12] 是躯干侧边，闭合胸腔——没有它们人物只是一堆挂在脊柱上的肢体 */
export const BONES: Edge[] = [
  [1, 0], [1, 2], [2, 3], [3, 4], [1, 5], [5, 6], [6, 7], [1, 8],
  [8, 9], [9, 10], [10, 11], [8, 12], [12, 13], [13, 14], [2, 9], [5, 12],
];

/** 把 N 个点尽量均匀地分配到每根骨头上 */
const ALLOC = (() => {
  const a: number[] = [];
  const b = Math.floor(N / BONES.length);
  let r = N - b * BONES.length;
  for (let i = 0; i < BONES.length; i++) a.push(b + (r-- > 0 ? 1 : 0));
  return a;
})();

export const SPAN: Edge[] = (() => {
  const s: Edge[] = [];
  let k = 0;
  for (const n of ALLOC) { s.push([k, k + n]); k += n; }
  return s;
})();

const smooth = (u: number) => u * u * (3 - 2 * u);

/** 给定时间取一帧关节位置（15 个关节，归一化坐标） */
export function poseJoints(t: number): Pt[] {
  const u = smooth((Math.sin(t) + 1) / 2);
  const bob = 0.012 * Math.sin(t * 2 + 0.6);
  const J: Pt[] = [];
  for (let i = 0; i < JN; i++) {
    J.push({
      x: KA[i][0] + (KB[i][0] - KA[i][0]) * u,
      y: KA[i][1] + (KB[i][1] - KA[i][1]) * u + bob,
    });
  }
  return J;
}

export const poseT: Pt[] = Array.from({ length: N }, () => ({ x: 0, y: 0 }));

/** 把关节位置铺开成 N 个点：每根骨头上的点沿骨等距排布 */
export function fillPose(J: Pt[]) {
  for (let b = 0; b < BONES.length; b++) {
    const [s, e] = SPAN[b];
    const n = e - s;
    const A = J[BONES[b][0]];
    const B = J[BONES[b][1]];
    for (let k = 0; k < n; k++) {
      const u = n === 1 ? 0.5 : k / (n - 1);
      poseT[s + k].x = A.x + (B.x - A.x) * u;
      poseT[s + k].y = A.y + (B.y - A.y) * u;
    }
  }
}

export const poseE: Edge[] = (() => {
  const E: Edge[] = [];
  for (const [s, e] of SPAN) for (let k = s; k < e - 1; k++) E.push([k, k + 1]);
  return E;
})();

/* ────────────────────────────────────────────────────────────────
   RIG 2 · AGENT GRAPH —— 智能体编排图：4 层节点 + 有向边
   每个节点用一圈 7 个点画成环，边上再插 2 个点，凑满 N。
   ──────────────────────────────────────────────────────────────── */
export const graphT: Pt[] = [];
export const graphE: Edge[] = [];
export const gNodes: { x: number; y: number; l: string }[] = [];
export const gLinks: { a: number; b: number; ph: number }[] = [];
export const gRing: number[] = [];

(function buildGraph() {
  const xs = [-0.3, -0.1, 0.1, 0.3];
  const L = [[0], [-0.175, 0, 0.175], [-0.235, -0.117, 0, 0.117, 0.235], [-0.155, 0, 0.155]];
  const LB = [
    ['接收'],
    ['规划', '路由', '记忆'],
    ['工具', '检索', '写码', '联网', '执行'],
    ['评估', '交付', '留痕'],
  ];
  L.forEach((ys, li) => ys.forEach((y, k) => gNodes.push({ x: xs[li], y, l: LB[li][k] })));

  const R = 7;
  const r = 0.028;
  gNodes.forEach((nd) => {
    gRing.push(graphT.length);
    for (let k = 0; k < R; k++) {
      const a = (k / R) * 6.28318 - 1.5708;
      graphT.push({ x: nd.x + Math.cos(a) * r, y: nd.y + Math.sin(a) * r });
    }
  });
  for (let i = 0; i < gNodes.length; i++) {
    const s = gRing[i];
    for (let k = 0; k < R; k++) graphE.push([s + k, s + ((k + 1) % R)]);
  }

  const NE: Edge[] = [
    [0, 1], [0, 2], [0, 3], [1, 4], [1, 5], [2, 5], [2, 6], [2, 7], [3, 7],
    [3, 8], [4, 9], [5, 9], [5, 10], [6, 10], [7, 10], [7, 11], [8, 11], [6, 11],
  ];
  NE.forEach(([a, b]) => {
    const A = gNodes[a];
    const B = gNodes[b];
    const i0 = graphT.length;
    graphT.push({ x: A.x + (B.x - A.x) * 0.36, y: A.y + (B.y - A.y) * 0.36 });
    graphT.push({ x: A.x + (B.x - A.x) * 0.64, y: A.y + (B.y - A.y) * 0.64 });
    graphE.push([gRing[a], i0], [i0, i0 + 1], [i0 + 1, gRing[b]]);
    gLinks.push({ a, b, ph: Math.random() });
  });

  while (graphT.length < N) graphT.push({ x: 0, y: 0 });
  graphT.length = N;
})();

/* ────────────────────────────────────────────────────────────────
   RIG 3 · QUANT —— 权益曲线 + 下方成交柱
   序列由固定种子的 LCG 生成，保证每次加载形状一致（可复现，
   而不是每次刷新都变一条随机线——那会显得是装饰而不是数据）。
   ──────────────────────────────────────────────────────────────── */
export const quantT: Pt[] = [];
export const quantE: Edge[] = [];
export const quantEbar: Edge[] = [];
export const CN = 76;
export let peakI = 0;
export let troughI = 0;
export let MDDPCT = '';

(function buildQuant() {
  let sd = 198;
  const rnd = () => ((sd = (sd * 16807) % 2147483647) / 2147483647);
  let v = 0.02;
  const vs: number[] = [];
  for (let i = 0; i < CN; i++) {
    v += (rnd() - 0.4) * 0.03;
    v = Math.max(-0.06, Math.min(0.34, v));
    vs.push(v);
  }
  vs.forEach((val, i) => quantT.push({ x: -0.3 + (0.6 * i) / (CN - 1), y: 0.19 - val }));
  for (let i = 0; i < CN - 1; i++) quantE.push([i, i + 1]);

  let peak = -9;
  let pi = 0;
  let mdd = 0;
  for (let i = 0; i < CN; i++) {
    if (vs[i] > peak) { peak = vs[i]; pi = i; }
    const dd = peak - vs[i];
    if (dd > mdd) { mdd = dd; peakI = pi; troughI = i; }
  }

  const B = (N - CN) >> 1;
  for (let i = 0; i < B; i++) {
    const x = -0.285 + (0.57 * i) / (B - 1);
    const h = 0.014 + rnd() * 0.042;
    const i0 = quantT.length;
    quantT.push({ x, y: 0.325 });
    quantT.push({ x, y: 0.325 - h });
    quantEbar.push([i0, i0 + 1]);
  }
  while (quantT.length < N) quantT.push({ x: 0.3, y: 0.325 });
  quantT.length = N;
  MDDPCT = '−' + ((mdd / 0.085) * 10).toFixed(1) + '%';
})();
